
import { useState } from "react";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import Card from "@mui/material/Card";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import bgImage from "assets/images/bg-sign-up-cover.jpeg";
import AuthService from "services/auth-service";
import CircularProgress from "@mui/material/CircularProgress";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function RequestDemo() {
  const [inputs, setInputs] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const [captchaToken, setCaptchaToken] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);


  const [errors, setErrors] = useState({
    nameError: false,
    emailError: false,
    companyError: false,
    messageError: false,
    error: false,
    errorText: "",
  });

  const changeHandler = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (inputs.name.trim().length === 0) {
      setErrors({ ...errors, nameError: true });
      return;
    }

    if (inputs.email.trim().length === 0 || !inputs.email.trim().match(mailFormat)) {
      setErrors({ ...errors, emailError: true });
      return;
    }

    if (inputs.company.trim().length === 0) {
      setErrors({ ...errors, companyError: true });
      return;
    }

    if (inputs.message.trim().length === 0) {
      setErrors({ ...errors, messageError: true });
      return;
    }

    if (!captchaToken) {
      setErrors({ ...errors, error: true, errorText: "Please verify you are not a robot." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        data: {
          type: "lead",
          attributes: {
            name: inputs.name,
            email: inputs.email,
            company: inputs.company,
            message: inputs.message,
            recaptchaToken: captchaToken,
          },
        },
      };

      await AuthService.requestDemo(payload);

      setInputs({ name: "", email: "", company: "", message: "" });
      setCaptchaToken(null);
      setSuccessMessage("Thank you! Your demo request has been submitted successfully.");
      setErrors({
        nameError: false,
        emailError: false,
        companyError: false,
        messageError: false,
        error: false,
        errorText: "",
      });
    } catch (err) {
      setErrors({ ...errors, error: true, errorText: err.message });
      console.error(err);
    } finally {
      setIsSubmitting(false); // Stop spinner
    }
  };

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          mb={1}
          pt={1}
          pb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Request a Demo
          </MDTypography>

          <MDBox display="flex" flexDirection="row" alignItems="center" justifyContent="center" my={2} gap={1} px={3}>
            <MDBox display="flex" alignItems="center">
              <CheckCircleIcon sx={{ color: "white", fontSize: "1rem" }} />
              <MDTypography variant="button" color="white" ml={1} fontWeight="medium" sx={{ fontSize: "0.80rem" }}>
                Hands-on Workshop
              </MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center">
              <CheckCircleIcon sx={{ color: "white", fontSize: "1rem" }} />
              <MDTypography variant="button" color="white" ml={1} fontWeight="medium" sx={{ fontSize: "0.80rem" }}>
                Zero commitment
              </MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center">
              <CheckCircleIcon sx={{ color: "white", fontSize: "1rem" }} />
              <MDTypography variant="button" color="white" ml={1} fontWeight="medium" sx={{ fontSize: "0.80rem" }}>
                No cost
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <MDBox mb={2}>
              <MDInput type="text" label="Full Name" variant="standard" fullWidth name="name" value={inputs.name} onChange={changeHandler} error={errors.nameError} />
            </MDBox>
            <MDBox mb={2}>
              <MDInput type="email" label="Email" variant="standard" fullWidth name="email" value={inputs.email} onChange={changeHandler} error={errors.emailError} />
            </MDBox>
            <MDBox mb={2}>
              <MDInput type="text" label="Company Name" variant="standard" fullWidth name="company" value={inputs.company} onChange={changeHandler} error={errors.companyError} />
            </MDBox>
            <MDBox mb={2}>
              <MDInput type="text" label="Message" variant="standard" fullWidth multiline rows={3} name="message" value={inputs.message} onChange={changeHandler} error={errors.messageError} />
            </MDBox>
            <MDBox mb={0} sx={{ width: "100%", display: "flex", justifyContent: "left" }}>
              <div
                style={{
                  transform: "scale(0.85)", // default scale for small screens
                  transformOrigin: "0 0",
                  width: "100%",
                  maxWidth: "304px", // reCAPTCHA's default width
                }}
              >
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                />
              </div>
            </MDBox>

            {successMessage && (
              <MDTypography variant="caption" color="success" fontWeight="medium">
                {successMessage}
              </MDTypography>
            )}
            {errors.error && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                {errors.errorText}
              </MDTypography>
            )}
            <MDBox mt={1} mb={1}>
              <MDButton variant="contained" color="accent" fullWidth type="submit" disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={24} color="accent" /> : "Request a Demo"}
              </MDButton>
            </MDBox>
            <MDBox mt={0} mb={0} textAlign="center">
              <MDTypography variant="button" color="text">
                Want to explore?{" "}
                <MDTypography component={Link} to="/auth/register" variant="button" color="info" fontWeight="medium" textGradient>
                  Start Free Trial
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mb={0} textAlign="center">
              <MDTypography variant="button" color="text">
                Go back to{" "}
                <MDTypography component="a" href={process.env.REACT_APP_URL_LANDING || "https://www.comparedly.com"} variant="button" color="info" fontWeight="medium" textGradient target="_blank" rel="noopener noreferrer">
                  Comparedly
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default RequestDemo;



